import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BudgetPost as BudgetPostId, InvoiceDto } from '@/api/generated';
import { useBudget, useBudgetEstimates, useExportBudgetCsv, useInvoices } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { BUDGET_POSTS as DOMAIN_BUDGET_POSTS } from '@/domain/budgetPosts';
import { formatNumberNlBE, toNumber } from '@/lib/format';
import styles from './Budget.module.css';
import { BudgetEstimatesDialog } from './BudgetEstimatesDialog';
import { InvoiceCreateDialog } from './InvoiceDialogs';

// 2026-05-17 — every per-post number on this page used to come from a
// static BUDGET_POSTS array hard-coded with values like
// `Ruwbouw plan=158000 spent=142380 note="+€4 200 meerwerk"`. They
// rendered on every project regardless of the real invoices booked. Now:
//   * The catalog of posts (labels + slugs) comes from the shared
//     web/src/domain/budgetPosts.ts module (single source of truth).
//   * Per-post "spent" is summed live from real invoices grouped by
//     `invoice.budgetPost`.
//   * There is no per-post "plan" field on the API today (only the
//     project-level Budget.Total), so the page no longer claims a plan
//     per post or a per-post saldo. The post table shows label + spent +
//     invoice count and links to the drilldown.

const STATUS_KIND = { done: 'ok', doing: 'warn', idle: 'idle' } as const;

interface KpiProps {
  label: string;
  value: number;
  unit: string;
  sub: string;
  kind?: 'warn' | 'ok' | 'bad';
}

function Kpi({ label, value, unit, sub, kind }: KpiProps) {
  return (
    <Card className={styles.kpi}>
      <div className="eyebrow">{label}</div>
      <div className={`row gap-4 ${styles.kpiValue}`}>
        <span className={styles.kpiUnit}>{unit}</span>
        <span className={styles.kpiNumber}>{formatNumberNlBE(value)}</span>
      </div>
      <div className={`mono ${styles.kpiSub} ${kind ? `c-${kind}` : ''}`}>{sub}</div>
    </Card>
  );
}

interface PostRow {
  id: BudgetPostId | null;
  slug: string;
  label: string;
  spent: number;
  expected: number;
  count: number;
  status: 'done' | 'doing' | 'idle';
}

function aggregateByPost(invoices: InvoiceDto[], expectedByPost: Map<string, number>): PostRow[] {
  // Pre-seed every catalog post so the table renders the full list even
  // when there are no invoices yet.
  const map = new Map<string, PostRow>();
  for (const meta of DOMAIN_BUDGET_POSTS) {
    map.set(meta.id, {
      id: meta.id,
      slug: meta.slug,
      label: meta.label,
      spent: 0,
      expected: expectedByPost.get(meta.id) ?? 0,
      count: 0,
      status: 'idle',
    });
  }
  // "Ongesorteerd" bucket for invoices without a budgetPost.
  map.set('__unassigned__', {
    id: null,
    slug: '',
    label: 'Ongesorteerd',
    spent: 0,
    expected: 0,
    count: 0,
    status: 'idle',
  });

  for (const inv of invoices) {
    const key = inv.budgetPost ?? '__unassigned__';
    const row = map.get(key);
    if (!row) continue;
    row.spent += toNumber(inv.amount);
    row.count += 1;
    // Paid invoices → "done" if every invoice on the post is paid; otherwise
    // "doing" while at least one invoice exists.
    row.status = 'doing';
  }

  // Hide the Ongesorteerd bucket when it's empty so a fresh project doesn't
  // show a confusing extra row.
  const unassigned = map.get('__unassigned__');
  if (unassigned && unassigned.count === 0) {
    map.delete('__unassigned__');
  }

  return Array.from(map.values());
}

export function Budget() {
  const budget = useBudget();
  // Pull ALL invoices for per-post aggregation. The pageSize cap should
  // cover a typical residential project comfortably.
  const allInvoices = useInvoices({ pageSize: 500 });
  const openInvoices = useInvoices({ status: 'Open', pageSize: 500 });
  const paidInvoices = useInvoices({ status: 'Paid', pageSize: 500 });
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [estimatesOpen, setEstimatesOpen] = useState(false);
  const exportCsv = useExportBudgetCsv();
  const estimates = useBudgetEstimates();
  const expectedByPost = new Map<string, number>(
    (estimates.data ?? []).map((e) => [e.post as string, toNumber(e.expected)]),
  );

  const postRows = aggregateByPost(allInvoices.data?.items ?? [], expectedByPost);
  const totalSpent = postRows.reduce((s, p) => s + p.spent, 0);
  const totalExpected = postRows.reduce((s, p) => s + p.expected, 0);
  const totalInvoices = postRows.reduce((s, p) => s + p.count, 0);

  return (
    <AppFrame>
      <div className={`row ${styles.headerRow}`}>
        <div>
          <div className="eyebrow">Budget &amp; financiën</div>
          <h1 className={styles.title}>Budget</h1>
          <p className={styles.subtitle}>
            {DOMAIN_BUDGET_POSTS.length} budgetposten · {totalInvoices} facturen (
            {toNumber(openInvoices.data?.total ?? 0)} openstaand ·{' '}
            {toNumber(paidInvoices.data?.total ?? 0)} betaald)
          </p>
        </div>
        <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
          <Btn onClick={() => setEstimatesOpen(true)}>
            <Icon name="receipt" size={14} /> Verwachte kosten
          </Btn>
          <Btn onClick={() => navigate('/meerwerken')}>
            <Icon name="receipt" size={14} /> Meerwerk
          </Btn>
          <Btn onClick={() => exportCsv.mutate()} disabled={exportCsv.isPending}>
            <Icon name="download" size={14} /> {exportCsv.isPending ? 'Bezig…' : 'Export'}
          </Btn>
          <Btn variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="plus" size={14} /> Factuur
          </Btn>
        </div>
      </div>
      <InvoiceCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <BudgetEstimatesDialog open={estimatesOpen} onOpenChange={setEstimatesOpen} />

      <QueryBoundary query={budget} loading="Budget laden…">
        {(b) => {
          const total = toNumber(b.total);
          const openTotal = (openInvoices.data?.items ?? []).reduce(
            (s, i) => s + toNumber(i.amount),
            0,
          );
          const paidTotal = (paidInvoices.data?.items ?? []).reduce(
            (s, i) => s + toNumber(i.amount),
            0,
          );
          // "Reeds besteed" = everything booked, open or paid. The backend
          // `Budget.Spent` only updates on invoice approval (ApproveInvoiceHandler.cs)
          // so a freshly booked Open invoice wouldn't show up if we used that.
          // Aggregating client-side from `allInvoices` keeps the KPI live.
          const committed = paidTotal + openTotal;
          const remaining = total > 0 ? Math.max(0, total - committed) : 0;
          return (
            <div className={styles.kpiStrip}>
              <Kpi
                label="Totaal voorzien"
                value={total}
                unit="€"
                sub={total > 0 ? 'budget ingesteld via Instellingen' : 'nog niet ingesteld'}
              />
              <Kpi
                label="Reeds besteed"
                value={committed}
                unit="€"
                sub={
                  total > 0
                    ? `${Math.round((committed / Math.max(1, total)) * 100)}% van budget`
                    : `${formatNumberNlBE(totalInvoices)} facturen`
                }
              />
              <Kpi
                label="Reeds betaald"
                value={paidTotal}
                unit="€"
                sub={`${toNumber(paidInvoices.data?.total ?? 0)} facturen`}
              />
              <Kpi
                label="Verwacht over"
                value={remaining}
                unit="€"
                sub={`€${formatNumberNlBE(openTotal)} openstaand`}
                kind={total > 0 ? 'warn' : undefined}
              />
            </div>
          );
        }}
      </QueryBoundary>

      <Card className={styles.tableCard}>
        <div className={`row gap-12 ${styles.tableHead}`}>
          <div className={styles.colPost}>Budgetpost</div>
          <div className={styles.colProg}>Besteed t.o.v. raming</div>
          <div className={styles.colAmount}>Verwacht</div>
          <div className={styles.colAmount}>Besteed</div>
          <div className={styles.colAmount}>Facturen</div>
          <div className={styles.colNav} />
        </div>
        {postRows.map((p, i) => {
          const last = i === postRows.length - 1;
          const hasExpected = p.expected > 0;
          const pct = hasExpected ? (p.spent / p.expected) * 100 : 0;
          const over = hasExpected && p.spent > p.expected;
          const body = (
            <>
              <div className={styles.colPost}>
                <div className="row gap-8">
                  <Pill kind={STATUS_KIND[p.status]} dot={false}>
                    {p.spent > 0 ? 'bezig' : 'wacht'}
                  </Pill>
                  <span className={styles.postName}>{p.label}</span>
                </div>
              </div>
              <div className={styles.colProg}>
                <div className={styles.progBar}>
                  <span
                    style={{
                      width: `${Math.min(100, pct)}%`,
                      background: !hasExpected ? 'var(--idle)' : over ? 'var(--bad)' : 'var(--ok)',
                    }}
                  />
                </div>
                <div className={`mono ${styles.progLabel} ${over ? 'c-bad' : ''}`}>
                  {hasExpected ? `${Math.round(pct)}% van raming` : 'raming niet ingesteld'}
                </div>
              </div>
              <div className={`mono ${styles.colAmount} ${styles.muted}`}>
                {p.expected > 0 ? `€${formatNumberNlBE(p.expected)}` : '—'}
              </div>
              <div className={`mono ${styles.colAmount} ${styles.spent}`}>
                €{formatNumberNlBE(p.spent)}
              </div>
              <div className={`mono ${styles.colAmount} ${styles.muted}`}>{p.count}</div>
              <div className={styles.colNav}>
                {p.slug && <Icon name="chevR" size={14} color="var(--ink-4)" />}
              </div>
            </>
          );
          // Posts in the catalog link to /budget/post/:slug. The
          // "Ongesorteerd" bucket (no slug) renders as a non-link row.
          if (!p.slug) {
            return (
              <div
                key={p.label}
                className={`row gap-12 ${styles.tableRow} ${last ? styles.tableRowLast : ''}`}
              >
                {body}
              </div>
            );
          }
          return (
            <Link
              key={p.label}
              to={`/budget/post/${p.slug}`}
              className={`row gap-12 ${styles.tableRow} ${last ? styles.tableRowLast : ''}`}
            >
              {body}
            </Link>
          );
        })}
        <div className={`row gap-12 ${styles.totalsRow}`}>
          <div className={styles.colPost}>Totaal</div>
          <div className={styles.colProg} />
          <div className={`mono ${styles.colAmount}`}>€{formatNumberNlBE(totalExpected)}</div>
          <div className={`mono ${styles.colAmount}`}>€{formatNumberNlBE(totalSpent)}</div>
          <div className={`mono ${styles.colAmount}`}>{totalInvoices}</div>
          <div className={styles.colNav} />
        </div>
      </Card>
    </AppFrame>
  );
}
